export default function GeneticTesting() {
  const steps = [
    {
      num: '01',
      icon: 'ri-stethoscope-line',
      title: 'Talk to Your Doctor',
      body: 'Ask your pediatrician or specialist about whole exome sequencing (WES). Mention the MAP3K7 gene and CSCF by name — most clinicians have never encountered it.',
      color: 'text-brand-500',
      bg: 'bg-brand-50',
      border: 'border-brand-100',
    },
    {
      num: '02',
      icon: 'ri-test-tube-line',
      title: 'Request Trio-WES',
      body: 'Trio whole exome sequencing — testing the child and both parents together — is the gold standard for identifying de novo MAP3K7 variants. It has confirmed every known CSCF case.',
      color: 'text-emerald-600',
      bg: 'bg-emerald-50',
      border: 'border-emerald-100',
    },
    {
      num: '03',
      icon: 'ri-file-search-line',
      title: 'Understand the Results',
      body: 'A genetic counselor can help interpret your results, explain what a MAP3K7 variant means for your child and family, and guide next steps for management and monitoring.',
      color: 'text-accent-500',
      bg: 'bg-accent-50',
      border: 'border-accent-100',
    },
    {
      num: '04',
      icon: 'ri-group-2-line',
      title: 'Connect & Contribute',
      body: 'A confirmed genetic diagnosis helps your child get the right care — and adds to the global body of knowledge. Every confirmed case matters for the entire CSCF community.',
      color: 'text-amber-600',
      bg: 'bg-amber-50',
      border: 'border-amber-100',
    },
  ];

  return (
    <section id="genetic-testing" className="py-20 bg-gray-50">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-8">

        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-emerald-50 border border-emerald-200 rounded-full mb-5">
              <i className="ri-test-tube-line text-emerald-600 text-xs"></i>
              <span className="text-xs font-semibold text-emerald-700 tracking-widest font-heading">GENETIC TESTING</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight mb-5 font-heading">
              Genetic testing is the only way to confirm CSCF.
            </h2>
            <p className="text-gray-600 text-base leading-relaxed font-body mb-4">
              Because CSCF shares features with other conditions like Noonan syndrome, a clinical diagnosis alone is not enough. <strong className="text-gray-800">Whole exome sequencing (WES)</strong> identifying a pathogenic variant in the <strong className="text-gray-800">MAP3K7 gene</strong> is required for a confirmed diagnosis.
            </p>
            <p className="text-gray-600 text-base leading-relaxed font-body">
              Without a confirmed genetic diagnosis, children may receive incomplete or misdirected care. A confirmed result unlocks the right specialists, the right monitoring protocols, and connection to the only community in the world that truly understands what your family is going through.
            </p>
          </div>

          {/* Right: urgent callout */}
          <div className="space-y-4">
            <div className="bg-white border-l-4 border-emerald-500 rounded-2xl p-6 flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-emerald-50 rounded-xl flex-shrink-0">
                <i className="ri-error-warning-line text-emerald-600 text-xl"></i>
              </div>
              <div>
                <p className="font-bold text-gray-900 font-heading mb-1">Clinical features alone are not enough</p>
                <p className="text-sm text-gray-500 font-body leading-relaxed">
                  CSCF overlaps significantly with Noonan syndrome, FMD2, and other RASopathies. Many children are misdiagnosed for years. Only a MAP3K7 variant on WES confirms CSCF.
                </p>
              </div>
            </div>
            <div className="bg-white border-l-4 border-brand-500 rounded-2xl p-6 flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-brand-50 rounded-xl flex-shrink-0">
                <i className="ri-parent-line text-brand-500 text-xl"></i>
              </div>
              <div>
                <p className="font-bold text-gray-900 font-heading mb-1">Most cases are de novo</p>
                <p className="text-sm text-gray-500 font-body leading-relaxed">
                  In most confirmed cases, the MAP3K7 variant arose spontaneously — it was not inherited from either parent. This means standard family history may not raise any red flags.
                </p>
              </div>
            </div>
            <div className="bg-white border-l-4 border-amber-400 rounded-2xl p-6 flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-xl flex-shrink-0">
                <i className="ri-heart-pulse-line text-amber-500 text-xl"></i>
              </div>
              <div>
                <p className="font-bold text-gray-900 font-heading mb-1">Early diagnosis changes outcomes</p>
                <p className="text-sm text-gray-500 font-body leading-relaxed">
                  Knowing the diagnosis early means cardiac monitoring, hearing intervention, and developmental therapies can begin sooner — when they have the greatest impact.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 4-step process */}
        <div className="mb-12">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest font-heading mb-8 text-center">
            How to pursue a genetic diagnosis
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map((step) => (
              <div
                key={step.num}
                className={`rounded-2xl border ${step.bg} ${step.border} p-6 flex flex-col`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 flex items-center justify-center bg-white rounded-xl flex-shrink-0`}>
                    <i className={`${step.icon} text-xl ${step.color}`}></i>
                  </div>
                  <span className={`text-xs font-bold font-heading ${step.color} tracking-widest`}>
                    STEP {step.num}
                  </span>
                </div>
                <h3 className="text-sm font-bold text-gray-900 font-heading mb-2">{step.title}</h3>
                <p className="text-xs text-gray-600 font-body leading-relaxed flex-grow">{step.body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA strip */}
        <div className="bg-gradient-to-r from-emerald-600 to-brand-600 rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-center md:text-left">
            <p className="text-white font-bold text-lg font-heading mb-1">
              Not sure where to start?
            </p>
            <p className="text-white/75 text-sm font-body">
              Our community includes families who have navigated the diagnostic journey. Ask them directly.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <a
              href="/science#diagnosis"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-emerald-700 font-bold rounded-full hover:bg-emerald-50 transition-colors whitespace-nowrap cursor-pointer font-body text-sm"
            >
              <i className="ri-microscope-line"></i>
              Read the Diagnosis Guide
            </a>
            <a
              href="https://www.facebook.com/share/g/1BT8NGBZVX/?mibextid=K35XfP"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/15 border-2 border-white/40 text-white font-semibold rounded-full hover:bg-white/25 transition-colors whitespace-nowrap cursor-pointer font-body text-sm"
            >
              <i className="ri-group-line"></i>
              Ask the Community
            </a>
          </div>
        </div>

      </div>
    </section>
  );
}
